/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useMemo } from 'react';

import { jsx } from '@atlaskit/css';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { ExtractInjectionAPI, UiComponentFactoryParams } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';

import type { AIPlugin } from '../../../aiPluginType';
import { getMonitorWatchmenCommands } from '../../../pm-plugins/ai-monitor-watchmen/commands';
import { useContentComponentContext } from '../../hooks/useContentComponentContext';
import {
	AnalyticsFlowContextProvider,
	useCreateAnalyticsFlow,
} from '../../utils/analytics/analyticsFlowUtils';

import { ADFStreamingManager } from './ADFStreamingManager';
import { DiscardSessionScreen } from './screens/Discard';
import { ErrorSessionScreen } from './screens/Error';
import { LoadingSessionScreen } from './screens/Loading';
import { PromptSessionScreen } from './screens/Prompt';
import { type Action, type State, useUIState } from './useUIState';
import { configItemUsesRovo } from './utils/checkMigrationStatus';

export interface SessionUIComponentProps {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	appearance: UiComponentFactoryParams['appearance'];
	providerFactory: ProviderFactory;
}

interface RenderScreenProps {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	appearance: UiComponentFactoryParams['appearance'];
	dispatch: React.Dispatch<Action>;
	isFreeGenConfigItem: boolean | undefined;
	providerFactory: ProviderFactory;
	uiState: State;
}

/**
 * RenderScreen is a separate component to prevent unnecessary unmount/remount cycles.
 * Defined outside SessionUIComponent to maintain a stable component reference.
 */
function RenderScreen({ api, appearance, dispatch, providerFactory, uiState }: RenderScreenProps) {
	switch (uiState) {
		case 'discard':
			return <DiscardSessionScreen api={api} appearance={appearance} dispatch={dispatch} />;

		case 'loading':
			return (
				<LoadingSessionScreen
					api={api}
					appearance={appearance}
					dispatch={dispatch}
					providerFactory={providerFactory}
				/>
			);

		case 'error':
			return <ErrorSessionScreen api={api} appearance={appearance} dispatch={dispatch} />;

		case 'prompt':
			return <PromptSessionScreen api={api} appearance={appearance} dispatch={dispatch} />;

		case 'idle':
			return null;
	}
}

/**
 * SessionUIComponent renders the appropriate screen based on the current UI state.
 * It provides AnalyticsFlowContext to child components that need analytics tracking.
 */
export function SessionUIComponent({ api, appearance, providerFactory }: SessionUIComponentProps) {
	const [uiState, dispatch] = useUIState(api);
	const { canInlineStreamChanges, isInDocumentStreamingEnabled, editorView } =
		useContentComponentContext();

	const { pluginState, isFreeGenConfigItem } = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(state) => {
			const decorationState = state.aiExperienceState?.aiDecorationExperiencePluginState;
			return {
				pluginState: decorationState,
				isFreeGenConfigItem:
					decorationState?.configItem && configItemUsesRovo(decorationState?.configItem?.key),
			};
		},
	);

	const monitorWatchmenCommands = useMemo(
		() =>
			fg('platform_editor_ai_aifc_patch_ga') ? getMonitorWatchmenCommands(editorView) : undefined,
		[editorView],
	);

	const invokeAttributes = useMemo(
		() => ({
			invokedFrom: pluginState?.lastTriggeredFrom,
			invokedFor: pluginState?.triggeredFor?.name ?? 'text',
			triggerMethod: pluginState?.triggerMethod,
			experienceName: pluginState?.configItem?.key,
			featureId: pluginState?.configItem?.featureId,
			aiGroupId: pluginState?.currentSessionId,
			aiSessionId: pluginState?.interactionSessionId ?? '',
		}),
		[pluginState],
	);

	const analyticsFlow = useCreateAnalyticsFlow({
		invokeAttributes,
		canInlineStreamChanges,
		isInDocumentStreamingEnabled,
		onCommonAttributesUpdated: monitorWatchmenCommands?.syncCommonAttributes,
	});

	return (
		<AnalyticsFlowContextProvider value={analyticsFlow}>
			<RenderScreen
				api={api}
				appearance={appearance}
				dispatch={dispatch}
				isFreeGenConfigItem={isFreeGenConfigItem}
				providerFactory={providerFactory}
				uiState={uiState}
			/>
			{isFreeGenConfigItem && fg('platform_editor_ai_migrate_from_xstate') && (
				<ADFStreamingManager />
			)}
		</AnalyticsFlowContextProvider>
	);
}
